/**
 * .resolve project files — a versioned JSON snapshot of a session.
 *
 * Explicit projects include full results; browser autosave omits large
 * reproducible analysis sets and transient undo snapshots. Both are read back
 * through `validatePersistedState`, which narrows untrusted data to
 * `PersistedState`.
 */

import {
  PERSISTED_KEYS,
  createDefaultPersistedState,
  type ApplySnapshot,
  type DetectionMethod,
  type PersistedState,
} from '@/store/persistedState';
import { normalizeWeight } from '@/logic/weights';
import type {
  ActiveTab,
  BlunderDetectionResults,
  DataSnoopingResults,
  MatchingMode,
  PointPair,
  SystemPoint,
  TransformationSolution,
  TransformedPoint,
} from '@/types';

export const PROJECT_FILE_VERSION = 2;

export interface ProjectFile {
  app: 'resolve';
  version: number;
  savedAt: string;
  data: PersistedState;
}

export const serializeProject = (data: PersistedState): string =>
  JSON.stringify(
    {
      app: 'resolve',
      version: PROJECT_FILE_VERSION,
      savedAt: new Date().toISOString(),
      data,
    } satisfies ProjectFile,
    null,
    2
  );

export type ParseProjectResult =
  | { ok: true; data: PersistedState }
  | { ok: false; error: string };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && isFinite(v);
const isFiniteNumberOrNull = (v: unknown): v is number | null =>
  v === null || isFiniteNumber(v);
const isString = (v: unknown): v is string => typeof v === 'string';
const isOptional = <T>(v: unknown, guard: (v: unknown) => v is T) =>
  v === undefined || guard(v);
const isPositiveId = (v: unknown): v is number =>
  isFiniteNumber(v) && Number.isSafeInteger(v) && v > 0;

const isPointLike = (p: unknown): p is SystemPoint =>
  isObject(p) &&
  isString(p.point) &&
  isString(p.north) &&
  isString(p.east) &&
  isOptional(p.description, isString) &&
  (p.system === 1 || p.system === 2);
const isPointList = (v: unknown): v is SystemPoint[] =>
  Array.isArray(v) && v.every(isPointLike);

type PairListResult =
  | { ok: true; pairs: PointPair[]; weightsAdjusted: boolean }
  | { ok: false; error: string };

const MALFORMED_POINTS = 'Project file has malformed point data.';
const INVALID_PAIR_IDS =
  'Project file has duplicate or invalid point pair identifiers (each pair needs a unique positive whole-number id).';

/**
 * Narrow untrusted point pairs. Ids must be unique positive integers (id 0 or
 * duplicates would break selection and result references); weights are
 * snapped to Ignore/Free/Unity/Fixed and non-finite weights are rejected.
 * Unknown keys are dropped.
 */
const narrowPointPairs = (value: unknown): PairListResult => {
  if (!Array.isArray(value)) return { ok: false, error: MALFORMED_POINTS };
  const ids = new Set<number>();
  const pairs: PointPair[] = [];
  let weightsAdjusted = false;
  for (const pair of value) {
    if (!isObject(pair)) return { ok: false, error: MALFORMED_POINTS };
    const { id, label, sys1Point, sys2Point, n1, e1, n2, e2 } = pair;
    const { desc1, desc2, matchDistance, detectedOffset } = pair;
    const weight = normalizeWeight(pair.weight);
    if (
      !isString(label) ||
      !isString(sys1Point) ||
      !isString(sys2Point) ||
      !isString(n1) ||
      !isString(e1) ||
      !isString(n2) ||
      !isString(e2) ||
      weight === null
    )
      return { ok: false, error: MALFORMED_POINTS };
    if (!isPositiveId(id) || ids.has(id))
      return { ok: false, error: INVALID_PAIR_IDS };
    ids.add(id);
    if (weight !== pair.weight) weightsAdjusted = true;
    const narrowed: PointPair = {
      id,
      label,
      sys1Point,
      sys2Point,
      n1,
      e1,
      n2,
      e2,
      weight,
    };
    if (isString(desc1)) narrowed.desc1 = desc1;
    if (isString(desc2)) narrowed.desc2 = desc2;
    if (isFiniteNumber(matchDistance)) narrowed.matchDistance = matchDistance;
    if (isFiniteNumber(detectedOffset))
      narrowed.detectedOffset = detectedOffset;
    pairs.push(narrowed);
  }
  return { ok: true, pairs, weightsAdjusted };
};

/** Pairs that are already valid and need no weight snapping. */
const isExactPairList = (v: unknown): v is PointPair[] => {
  const result = narrowPointPairs(v);
  return result.ok && !result.weightsAdjusted;
};

const isSolutionLike = (v: unknown): v is TransformationSolution | null =>
  v === null ||
  (isObject(v) &&
    [
      'a',
      'b',
      'c',
      'd',
      'scale',
      'rotation',
      'translationE',
      'translationN',
      'rmsN',
      'rmsE',
      'rmsTotal',
      'pointCount',
      'redundancy',
    ].every((key) => isFiniteNumber(v[key])) &&
    Array.isArray(v.residuals) &&
    v.residuals.every(
      (r) =>
        isObject(r) &&
        isFiniteNumber(r.id) &&
        ['vn', 've', 'total'].every((key) => isFiniteNumberOrNull(r[key])) &&
        typeof r.status === 'string'
    ));

const isBlunderRow = (v: unknown): boolean =>
  isObject(v) &&
  Array.isArray(v.rejectedIds) &&
  v.rejectedIds.every(isFiniteNumber) &&
  Array.isArray(v.rejectedLabels) &&
  v.rejectedLabels.every(isString) &&
  ['rmsTotal', 'maxResidual', 'scale', 'rotation'].every((key) =>
    isFiniteNumber(v[key])
  );
const isBlunderLike = (v: unknown): v is BlunderDetectionResults | null =>
  v === null ||
  (isObject(v) &&
    (v.error !== undefined
      ? isString(v.error)
      : (v.results === undefined ||
          (Array.isArray(v.results) && v.results.every(isBlunderRow))) &&
        (v.summary === undefined ||
          (Array.isArray(v.summary) &&
            v.summary.every(
              (row) =>
                isBlunderRow(row) && isObject(row) && isFiniteNumber(row.level)
            ))) &&
        (v.initial === undefined || isBlunderRow(v.initial))));
const isSnoopingLike = (v: unknown): v is DataSnoopingResults | null =>
  v === null ||
  (isObject(v) &&
    (v.error !== undefined
      ? isString(v.error)
      : Array.isArray(v.finalRejectedIds) &&
        v.finalRejectedIds.every(isFiniteNumber) &&
        isFiniteNumber(v.alpha) &&
        isFiniteNumber(v.criticalValue) &&
        Array.isArray(v.iterations) &&
        v.iterations.every(
          (row) =>
            isObject(row) &&
            isFiniteNumber(row.iteration) &&
            isFiniteNumberOrNull(row.rejectedPointId) &&
            isSolutionLike(row.solution) &&
            isBool(row.passed)
        ) &&
        (v.initialSolution === undefined ||
          isSolutionLike(v.initialSolution)) &&
        (v.finalSolution === undefined || isSolutionLike(v.finalSolution))));

const DETECTION_METHODS: readonly DetectionMethod[] = [
  null,
  'combinatorial',
  'dataSnooping',
];
const isDetectionMethod = (v: unknown): v is DetectionMethod =>
  DETECTION_METHODS.some((method) => method === v);

/** An undo snapshot is usable only if its pairs pass the same checks unchanged. */
const isApplySnapshotLike = (v: unknown): v is ApplySnapshot | null =>
  v === null ||
  (isObject(v) &&
    isExactPairList(v.pointPairs) &&
    isSolutionLike(v.solution) &&
    isFiniteNumberOrNull(v.solutionTimestamp) &&
    isFiniteNumberOrNull(v.appliedScale) &&
    isString(v.customScale) &&
    isBlunderLike(v.blunderResults) &&
    isSnoopingLike(v.dataSnoopingResults) &&
    isDetectionMethod(v.activeDetectionMethod) &&
    isBool(v.solutionStale) &&
    isBool(v.combinatorialStale) &&
    isBool(v.dataSnoopingStale));

const isTransformedPointList = (v: unknown): v is TransformedPoint[] =>
  Array.isArray(v) &&
  v.every(
    (p) =>
      isObject(p) &&
      ['originalPoint', 'newPoint', 'n1', 'e1', 'n2', 'e2', 'stdDev'].every(
        (key) => isString(p[key])
      ) &&
      isOptional(p.description, isString)
  );

const MATCHING_MODES: readonly MatchingMode[] = [
  'auto',
  'exact',
  'offset',
  'prefix',
  'nearest',
];
const TABS: readonly ActiveTab[] = [
  'import',
  'pairs',
  'results',
  'blunder',
  'transform',
];
const UNITS = ['unspecified', 'm', 'international ft', 'US survey ft'];

type FieldGuards = {
  [K in keyof PersistedState]: (v: unknown) => v is PersistedState[K];
};

/** A type guard for every durable key. */
const FIELD_VALIDATORS: FieldGuards = {
  inputRevision: isFiniteNumber,
  solutionRevision: isFiniteNumber,
  solutionInputRevision: isFiniteNumberOrNull,
  appliedScale: isFiniteNumberOrNull,
  transformedFromRevision: isFiniteNumberOrNull,
  transformedTimestamp: isFiniteNumberOrNull,
  transformedStale: isBool,
  units: (v): v is string => isString(v) && UNITS.includes(v),
  system1Points: isPointList,
  system2Points: isPointList,
  pointPairs: isExactPairList,
  solution: isSolutionLike,
  solutionStale: isBool,
  solutionTimestamp: isFiniteNumberOrNull,
  blunderResults: isBlunderLike,
  dataSnoopingResults: isSnoopingLike,
  combinatorialStale: isBool,
  dataSnoopingStale: isBool,
  activeDetectionMethod: isDetectionMethod,
  transformedPoints: isTransformedPointList,
  lastApplySnapshot: isApplySnapshotLike,
  rejectionLevel: isFiniteNumber,
  viewAllSolutions: isBool,
  runAllMaxLevel: isFiniteNumber,
  selectedAlpha: isFiniteNumber,
  activeTab: (v): v is ActiveTab => TABS.some((tab) => tab === v),
  customScale: isString,
  pointNumberOffset: isFiniteNumber,
  transformRange: (v): v is PersistedState['transformRange'] =>
    isObject(v) && isString(v.from) && isString(v.to),
  matchingMode: (v): v is MatchingMode =>
    MATCHING_MODES.some((mode) => mode === v),
  detectedOffset: isFiniteNumberOrNull,
};

/** Defaults for keys that are missing or malformed in loaded data. */
const loadDefaults = (): PersistedState => ({
  ...createDefaultPersistedState(),
  transformedStale: true,
  activeTab: 'pairs',
});

const PROVENANCE_KEYS = [
  'inputRevision',
  'solutionRevision',
  'solutionInputRevision',
  'solutionTimestamp',
  'appliedScale',
] as const;

/**
 * Narrow untrusted persisted data (a project file's `data` section or a
 * browser autosave) written by format `version`.
 *
 * - Point arrays must be well formed; pair ids must be unique positive
 *   integers. Otherwise the whole load is rejected.
 * - Pair weights are snapped to the four supported values. If any weight
 *   changed, retained results no longer describe the inputs and are marked
 *   outdated; the apply-undo snapshot is dropped.
 * - Every other key falls back to a safe default when missing or malformed.
 * - Results without verifiable provenance are marked outdated; version 1
 *   results always are.
 */
export const validatePersistedState = (
  raw: unknown,
  version: number
): ParseProjectResult => {
  if (!isObject(raw))
    return { ok: false, error: 'Project file is missing its data section.' };
  if (!isPointList(raw.system1Points) || !isPointList(raw.system2Points))
    return { ok: false, error: MALFORMED_POINTS };
  const pairs = narrowPointPairs(raw.pointPairs);
  if (!pairs.ok) return pairs;

  const data = loadDefaults();
  const take = <K extends keyof PersistedState>(key: K) => {
    const value = raw[key];
    const isValid: (v: unknown) => v is PersistedState[K] =
      FIELD_VALIDATORS[key];
    if (key in raw && isValid(value)) data[key] = value;
  };
  PERSISTED_KEYS.forEach(take);
  data.pointPairs = pairs.pairs;

  if (
    data.solution &&
    (!PROVENANCE_KEYS.every(
      (key) => key in raw && FIELD_VALIDATORS[key](raw[key])
    ) ||
      data.solutionInputRevision !== data.inputRevision)
  )
    data.solutionStale = true;
  if (pairs.weightsAdjusted) {
    data.solutionStale = true;
    data.combinatorialStale = true;
    data.dataSnoopingStale = true;
    data.transformedStale = true;
    data.lastApplySnapshot = null;
  }
  if (
    data.transformedPoints.length &&
    (!data.solution ||
      data.solutionStale ||
      data.transformedFromRevision == null ||
      data.transformedFromRevision !== data.solutionRevision ||
      data.transformedTimestamp == null)
  )
    data.transformedStale = true;
  if (version < 2) {
    data.solutionStale = !!data.solution;
    data.transformedStale = true;
    data.combinatorialStale = !!data.blunderResults;
    data.dataSnoopingStale = !!data.dataSnoopingResults;
    data.lastApplySnapshot = null;
  }
  return { ok: true, data };
};

export const parseProject = (content: string): ParseProjectResult => {
  let raw: unknown;
  try {
    raw = JSON.parse(content);
  } catch {
    return { ok: false, error: 'Not a valid .resolve file (invalid JSON).' };
  }

  if (!isObject(raw)) {
    return { ok: false, error: 'Not a valid .resolve file.' };
  }

  const { app, version } = raw;
  if (
    app !== 'resolve' ||
    !isFiniteNumber(version) ||
    !Number.isInteger(version) ||
    version < 1
  ) {
    return { ok: false, error: 'Not a Resolve project file.' };
  }
  if (version > PROJECT_FILE_VERSION) {
    return {
      ok: false,
      error: `This project was saved by a newer version of Resolve (file v${version}, app supports v${PROJECT_FILE_VERSION}). Update the app to open it.`,
    };
  }
  return validatePersistedState(raw.data, version);
};

export const projectFileName = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `resolve-project-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.resolve`;
};
